import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Configure API client
  useEffect(() => {
    api.onAuthError = () => {
      setUser(null);
    };
  }, []);

  // Check cookie-backed session on mount. The session token is HttpOnly,
  // so localStorage is only a legacy bearer-token fallback.
  useEffect(() => {
    loadUser();
  }, []);

  async function loadUser() {
    try {
      setLoading(true);
      const userData = await api.auth.getCurrentUser();
      setUser(userData);
    } catch (err) {
      console.error('Failed to load user:', err);
      api.setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(email, password) {
    try {
      setError(null);
      const result = await api.auth.login(email, password);
      if (result?.requiresMfa || result?.requiresMfaSetup) {
        return result;
      }
      setUser(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaLogin(attemptId, code) {
    try {
      setError(null);
      const userData = await api.auth.verifyMfaLogin(attemptId, code);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaBackupCode(attemptId, backupCode) {
    try {
      setError(null);
      const userData = await api.auth.verifyMfaBackupCode(attemptId, backupCode);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function sendMfaEmailCode(attemptId) {
    try {
      setError(null);
      return await api.auth.sendMfaEmailCode(attemptId);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyMfaEmailCode(attemptId, code) {
    try {
      setError(null);
      const userData = await api.auth.verifyMfaEmailCode(attemptId, code);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function verifyRequiredMfaSetup(attemptId, code) {
    try {
      setError(null);
      const result = await api.auth.verifyRequiredMfaSetup(attemptId, code);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function signup(email, password, name) {
    try {
      setError(null);
      const userData = await api.auth.signup(email, password, name);
      if (userData?.requiresMfaSetup) {
        return userData;
      }
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  async function logout() {
    try {
      await api.auth.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    verifyMfaLogin,
    verifyMfaBackupCode,
    sendMfaEmailCode,
    verifyMfaEmailCode,
    verifyRequiredMfaSetup,
    signup,
    logout,
    reloadUser: loadUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
